import CloseIcon from "@components/alert/Icons/CloseIcon.jsx";
import Select from "@layouts/v4/authPopup/components/Select/index.js";
import { Button, Form } from "antd";
import PropTypes from "prop-types";
import React, { useRef } from "react";
import { createPortal } from "react-dom";
import { getActiveLanguage } from "react-localize-redux";
import { useSelector } from "react-redux";
import { ActionsWrapper, FormWrapper, ModalOverlay, ModalTitle, ModalWrapper } from "./elements";

export const ShareChannelModal = ({
	isOpen,
	translate,
	titleKey = "chat.shareModal.title",
	channels,
	selectedChannelId,
	onChannelChange,
	isSubmitting,
	canSubmit,
	onClose,
	onSubmit,
}) => {
	const modalRef = useRef(null);
	const currentLanguage = useSelector((state) => getActiveLanguage(state.localize)?.code || "en");

	if (!isOpen) {
		return null;
	}

	const hasChannels = channels.length > 0;
	const locale = currentLanguage === "tr" ? "tr-TR" : "en-US";

	return createPortal(
		<ModalOverlay
			role="presentation"
			onClick={onClose}
			data-testid="chat-share-modal-overlay">
			<ModalWrapper
				innerRef={modalRef}
				role="dialog"
				aria-modal="true"
				onClick={(event) => event.stopPropagation()}>
				<ModalTitle data-testid="chat-share-modal-title">
					{translate(titleKey)}
				</ModalTitle>

				{hasChannels && (
					<FormWrapper>
						<Form.Item className="auth-form-item select">
							<Select
								value={selectedChannelId || undefined}
								onChange={onChannelChange}
								showSearch
								autocomplete="none"
								optionFilterProp="children"
								placeholder={translate("chat.selectChannel")}
								getPopupContainer={(triggerNode) => {
									const mountNode = modalRef.current;
									if (mountNode && typeof mountNode.appendChild === "function") {
										return mountNode;
									}

									return triggerNode?.parentNode || document.body;
								}}
								dropdownMatchSelectWidth
								dropdownStyle={{ zIndex: 10002 }}
								filterOption={(input, option) =>
									String(option.props.children)
										.toLowerCase()
										.indexOf(input.toLowerCase()) >= 0 ||
									String(option.props.value)
										.toLowerCase()
										.indexOf(input.toLowerCase()) >= 0
								}
								data-testid="chat-share-channel-select">
								{channels.map((channel) => (
									<Select.Option
										key={channel.channelId}
										value={channel.channelId}>
										{channel.label}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</FormWrapper>
				)}

				<ActionsWrapper>
					<Button
						type="primary"
						className="cancel-btn"
						data-testid="chat-share-cancel"
						onClick={onClose}>
						{translate("chat.tipModal.cancel").toLocaleUpperCase(locale)}
					</Button>
					<Button
						type="primary"
						className="complete-btn"
						disabled={!canSubmit || isSubmitting}
						data-testid="chat-share-submit"
						onClick={onSubmit}>
						{translate("chat.shareModal.send").toLocaleUpperCase(locale)}
					</Button>
				</ActionsWrapper>
			</ModalWrapper>

			<CloseIcon
				className="close-icon"
				data-testid="button-alert-close"
				onClick={onClose}
			/>
		</ModalOverlay>,
		document.body
	);
};

ShareChannelModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	translate: PropTypes.func.isRequired,
	titleKey: PropTypes.string,
	channels: PropTypes.arrayOf(
		PropTypes.shape({
			channelId: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
		})
	).isRequired,
	selectedChannelId: PropTypes.string.isRequired,
	onChannelChange: PropTypes.func.isRequired,
	isSubmitting: PropTypes.bool,
	canSubmit: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
};
